import React, { useRef } from "react"
import { useDispatch } from "react-redux"
import InputAdornment from "@mui/material/InputAdornment"
import dayjs from "dayjs"
import { Button, Dialog, DialogActions, TextField, Typography, styled, Stack } from "@mui/material"
import { Formik, Form, Field } from "formik"
import FormikIntervalCalendarField from "../../../components/form/FormikIntervalCalendarField"
import { COLORS, getTimezoneDate } from "../../../utils"
import { makeReproductionSchema } from "../../../utils/yupValidators"
import { regeneratePSEReproduction } from "../../../actions/ProductionSteps/ProductionStepExecutions"
import { openAppMessageSnackBar } from "../../../actions/Utils/app"

export const ReproductionDialogForm = ({ reproductionNeed, onClose, open }) => {
  const dispatch = useDispatch()

  const formikRef = useRef(null)
  const paperRef = useRef(null)

  const quickDateOptions = [
    { label: "Aujourd'hui", interval: 0, intervalUnit: "day", intervalDirection: "plus" },
    { label: "Demain", interval: 1, intervalUnit: "day", intervalDirection: "plus" },
    { label: "Après-demain", interval: 2, intervalUnit: "day", intervalDirection: "plus" }
  ]

  // Handles quick date option selection (e.g. "Today", "Tomorrow"). 
  const handleOptionAfterChange = (option, setFieldValue) => {
    const { interval, intervalUnit, intervalDirection } = option

    const targetDate = intervalDirection === "plus"
      ? dayjs().add(interval, intervalUnit)
      : dayjs().subtract(interval, intervalUnit)

    setFieldValue("stepProductionDate", getTimezoneDate(targetDate))
  }

  const onConfirm = async (values) => {
    try {
      await dispatch(regeneratePSEReproduction(reproductionNeed.originalPSEId, reproductionNeed.objectId, values))
      onClose()
      dispatch(openAppMessageSnackBar("La reprod a été créée avec succès."))
    } catch (e) {
      dispatch(openAppMessageSnackBar(e.message, "error"))
    }
  }

  return (
    <StyledDialog open={open} onClose={onClose} PaperProps={{ ref: paperRef }}>
      <Typography sx={sx.title}>{reproductionNeed.productionItemName}</Typography>
      <Formik
        innerRef={formikRef}
        initialValues={{ weight: "", stepProductionDate: null }}
        validationSchema={makeReproductionSchema(reproductionNeed.reproductionNetWeight)}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onConfirm}
      >
        {({ values, errors, setFieldValue }) => (
          <Form>
            <Stack spacing={3}>
              {/* Header */}
              <Typography sx={sx.subTitle}>
                {reproductionNeed.sectionName}<br />
                {reproductionNeed.uniqueCode} - {reproductionNeed.recipeName}
              </Typography>
              <Typography sx={sx.instruction}>
                Indiquez la quantité que vous souhaitez mettre en reprod et le jour de production.
              </Typography>

              {/* Missing weight */}
              <Stack spacing={1} sx={sx.blockSpacing}>
                <Typography sx={sx.helper}>Poids manquant</Typography>
                <Typography sx={sx.helper}>
                  <span style={{ color: COLORS.BLACK800 }}>
                    {Math.round(reproductionNeed.reproductionNetWeight * 100) / 100}
                  </span>
                  <span style={{ color: COLORS.BLACK_500 }}> kg</span>
                </Typography>
              </Stack>

              {/* Weight to reproduce */}
              <Stack pt={1}>
                <Typography sx={sx.label}>Quantité à reproduire</Typography>
                <Field
                  as={TextField}
                  name="weight"
                  variant="standard"
                  type="number"
                  inputProps={{
                    min: 0,
                    step: "any",
                    inputMode: "decimal",
                    pattern: "[0-9]+([.,][0-9]+)?"
                  }}
                  error={!!errors.weight}
                  helperText={errors.weight}
                  sx={{ ...sx.inputBase, "& .MuiInputBase-input": sx.inputInner }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={sx.adornText}>kg</Typography>
                      </InputAdornment>
                    )
                  }}
                />
              </Stack>


              <Stack pt={1}>
                <Typography sx={sx.label}>Jour de production</Typography>
                <FormikIntervalCalendarField
                  options={quickDateOptions}
                  handleChange={(picked) => setFieldValue("stepProductionDate", getTimezoneDate(picked))}
                  afterChangeOption={(option) => handleOptionAfterChange(option, setFieldValue)}
                  date={values.stepProductionDate}
                  startDate={dayjs().startOf("day").toDate()}
                  name="stepProductionDate"
                  hasError={!!errors.stepProductionDate}
                  errorText={errors.stepProductionDate}
                  popoverSettings={{
                    anchorOrigin: { vertical: "bottom", horizontal: "center" },
                    transformOrigin: { vertical: "top", horizontal: "center" },
                    PaperProps: { sx: { mt: "3px" } },
                  }}
                  sx={{
                    "& .MuiInputBase-input": sx.inputInner,
                    "& .MuiSvgIcon-root": sx.dateIcon
                  }}
                  showSelectionInBetween={false}
                />
              </Stack>

              {/* Footer */}
              <DialogActions sx={sx.actions}>
                <Button sx={sx.btnCancel} onClick={onClose} color="primary">
                  Annuler
                </Button>
                <Button sx={sx.btnSubmit} type="submit" color="primary" variant="contained">
                  Valider
                </Button>
              </DialogActions>
            </Stack>
          </Form>
        )}
      </Formik>
    </StyledDialog>
  )
}

export default ReproductionDialogForm

const StyledDialog = styled(Dialog)({
  "& .MuiPaper-root": {
    width: 555,
    minHeight: 527,
    padding: 32
  }
})

const sx = {
  title: {
    mb: "8px",
    fontSize: 18,
    color: COLORS.BLACK800,
    fontWeight: 500,
    lineHeight: 1.4
  },
  subTitle: {
    fontSize: 16,
    color: COLORS.LABEL_GREY,
    fontWeight: 400,
    lineHeight: 1.6
  },
  instruction: {
    fontSize: 16,
    color: COLORS.BLACK_500,
    fontWeight: 400,
    lineHeight: 1.375
  },
  blockSpacing: {
    pt: 1,
    pb: 1
  },
  label: {
    fontSize: 12,
    color: COLORS.BLACK_500,
    fontWeight: 400,
    lineHeight: 1.5
  },
  helper: {
    fontSize: 14,
    color: COLORS.BLACK_500,
    fontWeight: 400,
    lineHeight: 1.57
  },
  inputBase: { width: "100%" },
  inputInner: {
    fontSize: 14,
    color: COLORS.BLACK800,
    fontWeight: 400,
    lineHeight: 1.57,
    pb: "6px",
    pt: "3px",
    height: "22px"
  },
  adornText: {
    fontSize: 16,
    color: COLORS.BLACK_400,
    fontWeight: 400,
    lineHeight: 1.125
  },
  dateIcon: {
    fontSize: "24px"
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    p: 0,
    pt: 2
  },
  btnCancel: {
    p: "13px 10px",
    fontSize: "14px",
    lineHeight: 1
  },
  btnSubmit: {
    p: "13px 16px",
    fontSize: "14px",
    lineHeight: 1
  }
}